import React from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { StudentWithAverageAndLastPointDto } from "./types";

interface Student {
  id: number;
  name: string;
  surname: string;
  averagePoint: number;
  lastPoint: number;
}

interface TopStudentsSectionProps {
  students: StudentWithAverageAndLastPointDto[] | undefined;
}

export const TopStudentsSection: React.FC<TopStudentsSectionProps> = ({
  students,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!students) return null;

  const transformStudentData = (
    studentData: StudentWithAverageAndLastPointDto,
  ): Student => ({
    id: studentData.id ?? 0,
    name: studentData.name ?? "",
    surname: studentData.surname ?? "",
    averagePoint: studentData.averagePoint ?? 0,
    lastPoint: studentData.lastPoint ?? 0,
  });

  const transformedStudents = students
    .map(transformStudentData)
    .sort((a, b) => b.averagePoint - a.averagePoint)
    .slice(0, 5);

  const handleStudentClick = (studentId: number) => {
    navigate(`/students/${studentId}`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("analytics.charts.topStudents")}
          </h3>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {t("analytics.labels.avgScore")}
          </span>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
        {transformedStudents.map((student, index) => (
          <div
            key={student.id}
            className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            onClick={() => handleStudentClick(student.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleStudentClick(student.id);
              }
            }}
          >
            <div className="flex items-center space-x-4">
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  index === 0
                    ? "bg-yellow-100 text-yellow-600"
                    : index === 1
                      ? "bg-gray-100 text-gray-600"
                      : index === 2
                        ? "bg-orange-100 text-orange-600"
                        : "bg-blue-100 text-blue-600"
                }`}
              >
                {index + 1}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {student.name} {student.surname}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("analytics.labels.lastScore")}: {student.lastPoint ?? 0}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {student.lastPoint > student.averagePoint ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              <span className="font-medium text-gray-900 dark:text-white">
                {student.averagePoint?.toFixed(1) ?? 0}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopStudentsSection;
